import { api } from '../DefaultService';
import {
  IDeviceDownloadVideoParams,
  IDeviceDownloadVideoResponse,
  IDeviceFilterParams,
  IDeviceGetParams,
  IDeviceLprGroupsConfigParams,
  IDeviceLprGroupsConfigProps,
  IDeviceNetworkConfigParams,
  IDeviceNetworkConfigProps,
  IDeviceNtpConfigParams,
  IDeviceNtpConfigProps,
  IDeviceProps,
  IDeviceRecordingScheduleConfigParams,
  IDeviceRecordingScheduleConfigProps,
  IDeviceRecoverEventConfigParams,
  IDeviceRecoverEventConfigProps,
  IDeviceSimpleProps,
  IDeviceTimeConfigParams,
  IDeviceUpdateStatusParams,
  IDeviceVideoConfigParams,
  IDeviceVideoConfigProps,
  IGetLostAlarmEventsConfigParams,
  IRecognitionInternalGetParams,
  IRecognitionInternalProps,
  ISmtpDto,
} from './types';
import { IDvrDeviceSimpleProps } from './DvrService/types';

export const DEVICE_ENDPOINT = 'v1/devices';
export const DEVICE_CONTROLLER_ENDPOINT = 'v1/devices/controllers';
export const DEVICE_CAMS_ENDPOINT = 'v1/devices/cams';
export const DEVICE_DVRS_ENDPOINT = 'v1/devices/dvr';

export const updateStatusDeviceAsync = async ({
  id,
  data,
}: IDeviceUpdateStatusParams): Promise<void> => {
  await api.put(`${DEVICE_ENDPOINT}/${id}/status`, data).catch((error) => {
    throw new Error(
      error?.response?.data?.userMessage || 'Erro interno do servidor!',
    );
  });
};

export const createDeviceStream = async (deviceId: number) => {
  await api.post(`${DEVICE_ENDPOINT}/${deviceId}/stream`);
};

export const getDeviceByIdAsync = async (
  deviceId: number,
): Promise<IDeviceProps> => {
  const response = await api.get(`${DEVICE_ENDPOINT}/${deviceId}`);
  return response.data;
};

export const deleteDeviceByIdAsync = async (
  deviceId: number,
): Promise<void> => {
  const response = await api.delete(`${DEVICE_ENDPOINT}/${deviceId}`);
  return response.data;
};

export const getAllDevicesByLicenseInfoAsync = async ({
  pagination,
  filter,
}: IDeviceGetParams): Promise<IDeviceProps[]> => {
  const response = await api.get(`${DEVICE_ENDPOINT}/getAllByLicense/info`, {
    params: { ...pagination, ...filter },
  });
  return response.data;
};

export const getAllDevicesByLicenseAsync = async ({
  pagination,
  filter,
}: IDeviceGetParams): Promise<IDeviceSimpleProps[]> => {
  const response = await api.get(`${DEVICE_ENDPOINT}/getAllByLicense`, {
    params: { ...pagination, ...filter },
  });
  return response.data;
};

export const createDeviceAsync = async (data: any) => {
  try {
    const { data: response } = await api.post('/v1/devices', data);
    return response;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.userMessage || 'Erro ao criar dispositivo.',
    );
  }
};

export const updateDeviceAsync = async ({ id, data }: { id: number; data: any }) => {
  try {
    const { data: response } = await api.put(`/v1/devices/${id}`, data);
    return response;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.userMessage || 'Erro ao atualizar dispositivo.',
    );
  }
};

// Endpoint dedicado: o PUT geral (updateDeviceAsync) exige Name/Model, que não fazem parte dessa
// tela — mandar só {name, externalControlId} pra ele dava 400 por causa da validação implícita
// desses campos obrigatórios no backend.
export const updateDeviceExternalControlIdAsync = async ({
  id,
  externalControlId,
  moduleIp,
}: {
  id: number;
  externalControlId: string;
  moduleIp?: string;
}) => {
  try {
    const { data: response } = await api.patch(
      `/v1/devices/${id}/external-control-id`,
      { externalControlId, moduleIp },
    );
    return response;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.userMessage || 'Erro ao vincular o módulo ao dispositivo.',
    );
  }
};

export interface IModuleHistorySyncJobResponse {
  jobId: number;
  hangfireJobId?: string | null;
  message: string;
}

// Busca o histórico do módulo físico (http://IP/history.json) e reenvia, em background, os
// acionamentos que ainda não geraram vídeo — dispara um job e retorna na hora, o progresso é
// acompanhado via polling do job (mesmo padrão do reenvio em lote de eventos de acionamento).
export const syncDeviceModuleHistoryAsync = async (
  id: number,
): Promise<IModuleHistorySyncJobResponse> => {
  try {
    const { data: response } = await api.post(`/v1/devices/${id}/module-history-sync`);
    return response;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.userMessage || 'Erro ao sincronizar histórico do módulo.',
    );
  }
};

const FACIAL_CONTROLLER_ENDPOINT = 'v1/devices/cameras/facialController';

export interface IFacialSyncDeviceSelectionProps {
  id: number;
  name: string;
  brand: 'ControlID' | 'Intelbras';
  isOnline: boolean;
}

export interface IDeviceFacialSyncJobResponse {
  jobId: number;
  hangfireJobId?: string | null;
  message: string;
}

// Lista de devices ControlID/Intelbras da licença, pra seleção manual na tela de sincronização por
// dispositivo (rodar a sincronização de reconhecimento facial sob demanda em 1 ou mais devices, sem
// esperar o job agendado que roda em todos).
export const getFacialSyncSelectableDevicesAsync = async (): Promise<IFacialSyncDeviceSelectionProps[]> => {
  const { data: response } = await api.get(`${FACIAL_CONTROLLER_ENDPOINT}/sync-selection`);
  return response;
};

export const syncFacialRecognitionDevicesAsync = async (
  deviceIds: number[],
): Promise<IDeviceFacialSyncJobResponse> => {
  try {
    const { data: response } = await api.post(`${FACIAL_CONTROLLER_ENDPOINT}/sync`, { deviceIds });
    return response;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ||
      error?.response?.data?.userMessage ||
      'Erro ao sincronizar dispositivos selecionados.',
    );
  }
};

export const getAllCamsByLicenseAsync = async ({
  pagination,
  filter,
}: IDeviceGetParams): Promise<IDeviceSimpleProps[]> => {
  const response = await api.get(`${DEVICE_CAMS_ENDPOINT}/getAllByLicense`, {
    params: { ...pagination, ...filter },
  });
  return response.data;
};

export const getAllCamsByLicenseCountAsync = async ({
  filter,
}: IDeviceGetParams): Promise<number> => {
  const response = await api.get(
    `${DEVICE_CAMS_ENDPOINT}/getAllByLicense/count`,
    {
      params: { ...filter },
    },
  );
  return response.data;
};

export const getAllControllersByLicenseAsync = async ({
  pagination,
  filter,
}: IDeviceGetParams): Promise<IDeviceSimpleProps[]> => {
  const response = await api.get(
    `${DEVICE_CONTROLLER_ENDPOINT}/getAllByLicense`,
    {
      params: { ...pagination, ...filter },
    },
  );
  return response.data;
};

export const getAllControllersByLicenseCountAsync = async ({
  filter,
}: IDeviceGetParams): Promise<number> => {
  const response = await api.get(
    `${DEVICE_CONTROLLER_ENDPOINT}/getAllByLicense/count`,
    {
      params: { ...filter },
    },
  );
  return response.data;
};

export const getAllDvrByLicenseAsync = async ({
  pagination,
  filter,
}: IDeviceGetParams): Promise<
  IDeviceSimpleProps[] | IDvrDeviceSimpleProps[]
> => {
  const response = await api.get(`${DEVICE_DVRS_ENDPOINT}/getAllByLicense`, {
    params: { ...pagination, ...filter },
  });
  return response.data;
};

export const getAllDvrByLicenseCountAsync = async ({
  filter,
}: IDeviceGetParams): Promise<number> => {
  const response = await api.get(
    `${DEVICE_DVRS_ENDPOINT}/getAllByLicense/count`,
    {
      params: { ...filter },
    },
  );
  return response.data;
};

export const getAllDevicesByLicenseCountAsync = async (
  filter?: IDeviceFilterParams,
): Promise<number> => {
  return await api
    .get(`${DEVICE_ENDPOINT}/getAllByLicense/count`, {
      params: { ...filter },
    })
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const getAllInternalFacesFromDeviceAsync = async ({
  pagination,
  id,
}: IRecognitionInternalGetParams): Promise<IRecognitionInternalProps[]> => {
  return await api
    .get(`${DEVICE_ENDPOINT}/${id}/faces`, {
      params: pagination,
    })
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const getAllInternalFacesFromDeviceCountAsync = async (
  id: number,
): Promise<number> => {
  return await api
    .get(`${DEVICE_ENDPOINT}/${id}/faces/count`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const downloadDeviceVideoAsync = async ({
  deviceId,
  startTime,
  endTime,
  channel,
}: IDeviceDownloadVideoParams): Promise<IDeviceDownloadVideoResponse> => {
  try {
    const response = await api.post<IDeviceDownloadVideoResponse>(
      `${DEVICE_ENDPOINT}/${deviceId}/download`,
      {
        startTime,
        endTime,
        channel,
      },
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.userMessage ||
        'Erro ao baixar o vídeo do dispositivo!',
    );
  }
};

export const getNetworkConfigAsync = async (
  id: number,
): Promise<IDeviceNetworkConfigProps> => {
  return await api
    .get(`${DEVICE_ENDPOINT}/${id}/config/network`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const updateNetworkConfigAsync = async ({
  id,
  data,
}: IDeviceNetworkConfigParams): Promise<void> => {
  await api
    .put(`${DEVICE_ENDPOINT}/${id}/config/network`, data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const getRecoverEventConfigAsync = async (
  id: number,
): Promise<IDeviceRecoverEventConfigProps> => {
  return await api
    .get(`${DEVICE_ENDPOINT}/${id}/config/recoverEvent`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const updateRecoverEventConfigAsync = async ({
  id,
  data,
}: IDeviceRecoverEventConfigParams): Promise<void> => {
  await api
    .put(`${DEVICE_ENDPOINT}/${id}/config/recoverEvent`, data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const getNtpConfigAsync = async (
  id: number,
): Promise<IDeviceNtpConfigProps> => {
  return await api
    .get(`${DEVICE_ENDPOINT}/${id}/config/ntp`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};
export const openDoor = async (id: number): Promise<IDeviceNtpConfigProps> => {
  return await api
    .get(`${DEVICE_ENDPOINT}/${id}/openDoor`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};
export const updateNtpConfigAsync = async ({
  id,
  data,
}: IDeviceNtpConfigParams): Promise<void> => {
  await api.put(`${DEVICE_ENDPOINT}/${id}/config/ntp`, data).catch((error) => {
    throw new Error(
      error?.response?.data?.userMessage || 'Erro interno do servidor!',
    );
  });
};

export const getTimeConfigAsync = async (id: number): Promise<Date> => {
  return await api
    .get(`${DEVICE_ENDPOINT}/${id}/config/time`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const updateTimeConfigAsync = async ({
  id,
  data,
}: IDeviceTimeConfigParams): Promise<void> => {
  await api.put(`${DEVICE_ENDPOINT}/${id}/config/time`, data).catch((error) => {
    throw new Error(
      error?.response?.data?.userMessage || 'Erro interno do servidor!',
    );
  });
};

export const getVideoConfigAsync = async (
  id: number,
): Promise<IDeviceVideoConfigProps> => {
  return await api
    .get(`${DEVICE_ENDPOINT}/${id}/config/video`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const updateVideoConfigAsync = async ({
  id,
  data,
}: IDeviceVideoConfigParams): Promise<void> => {
  await api
    .put(`${DEVICE_ENDPOINT}/${id}/config/video`, data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const getRecordingScheduleConfigAsync = async (
  id: number,
): Promise<IDeviceRecordingScheduleConfigProps> => {
  return await api
    .get(`${DEVICE_ENDPOINT}/${id}/config/recordingSchedule`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const updateRecordingScheduleConfigAsync = async ({
  id,
  data,
}: IDeviceRecordingScheduleConfigParams): Promise<void> => {
  await api
    .put(`${DEVICE_ENDPOINT}/${id}/config/recordingSchedule`, data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const updateLprGroupsConfigAsync = async ({
  id,
  data,
}: IDeviceLprGroupsConfigParams): Promise<void> => {
  await api
    .put(`${DEVICE_ENDPOINT}/${id}/config/lprGroup`, data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const getLprGroupsConfigAsync = async (
  id: number,
): Promise<IDeviceLprGroupsConfigProps> => {
  return await api
    .get(`${DEVICE_ENDPOINT}/${id}/config/lprGroup`)
    .then((r) => r.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const getLostAlarmEventsConfigAsync = async ({
  id,
  channelId,
  daysAgo,
}: IGetLostAlarmEventsConfigParams): Promise<void> => {
  return await api
    .put(`${DEVICE_ENDPOINT}/${id}/config/recoverLostAlarmEvents`, {
      channelId,
      daysAgo,
    })
    .then((r) => r.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const getSmtpIntegrationsAsync = async (
  isActive?: boolean,
  includeId?: number,
): Promise<ISmtpDto[]> => {
  const SMTP_ENDPOINT = 'v1/integrations/smtp';
  const params: any = {};
  if (isActive !== undefined) params.isActive = isActive;
  if (includeId !== undefined) params.includeId = includeId;

  return await api
    .get(`${SMTP_ENDPOINT}/getAllByLicense`, { params })
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const getActiveSmtpIntegrationsAsync = async (): Promise<ISmtpDto[]> => {
  return getSmtpIntegrationsAsync(true);
};
